import React, { useRef, useEffect, useState } from "react";
import './letters.css';

const Lenia = ({ scale = 35, dt = 0.003, fps = 60 }) => {
  const canvasRef = useRef(null);
  const [dimensions, setDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    const handleResize = () =>
      setDimensions({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const width = dimensions.width;
    const height = dimensions.height;
    const sizeX = Math.floor(width / scale);
    const sizeY = Math.floor(height / scale);

    // Ring-shaped kernel
    const kernelRadius = 6;
    const ringRadius = 3;
    const sigma = 0.3; // thinner ring -> sharper structures
    const kernel = [];
    for (let y = -kernelRadius; y <= kernelRadius; y++) {
      for (let x = -kernelRadius; x <= kernelRadius; x++) {
        const r = Math.sqrt(x * x + y * y);
        const value = Math.exp(-((r - ringRadius) ** 2) / (2 * sigma ** 2));
        kernel.push({ x, y, value });
      }
    }

    // Grid initialization with small random blobs in the center
    const grid = Array.from({ length: sizeY }, (_, y) =>
      Array.from({ length: sizeX }, (_, x) => {
        const centerX = sizeX / 2;
        const centerY = sizeY / 2;
        const distance = Math.sqrt((x - centerX) ** 2 + (y - centerY) ** 2);
        const radius = Math.min(sizeX, sizeY) / 8;
        if (distance < radius) {
          return [Math.random(), Math.random(), Math.random()];
        } else {
          return [0, 0, 0]; // empty background
        }
      })
    );
    const nextGrid = Array.from({ length: sizeY }, () =>
      Array.from({ length: sizeX }, () => [0, 0, 0])
    );

    // Channel interaction matrix
    const interaction = [
      [1.0, 0.5, 0.2],
      [0.3, 1.0, 0.5],
      [0.2, 0.5, 1.0],
    ];

    // Smooth sigmoid-like growth
    const growthFunction = (sum) => {
      const m = 0.5;  // center of growth
      const s = 0.05; // width of growth
      return 2 * Math.exp(-((sum - m) ** 2) / (2 * s ** 2)) - 1;
    };

    const updateGrid = () => {
      for (let y = 0; y < sizeY; y++) {
        for (let x = 0; x < sizeX; x++) {
          for (let c = 0; c < 3; c++) {
            let sum = 0;
            kernel.forEach((k) => {
              const nx = (x + k.x + sizeX) % sizeX;
              const ny = (y + k.y + sizeY) % sizeY;
              for (let ci = 0; ci < 3; ci++) {
                sum += grid[ny][nx][ci] * k.value * interaction[c][ci];
              }
            });
            nextGrid[y][x][c] = Math.min(
              Math.max(grid[y][x][c] + dt * growthFunction(sum), 0),
              1
            );
          }
        }
      }

      // Swap grids
      for (let y = 0; y < sizeY; y++) {
        for (let x = 0; x < sizeX; x++) {
          grid[y][x][0] = nextGrid[y][x][0];
          grid[y][x][1] = nextGrid[y][x][1];
          grid[y][x][2] = nextGrid[y][x][2];
        }
      }
    };

    const drawGrid = (ctx) => {
      const imageData = ctx.createImageData(sizeX, sizeY);
      const sharpen = (v) => Math.pow(v, 0.01); // sqrt brightens and sharpens
      for (let y = 0; y < sizeY; y++) {
        for (let x = 0; x < sizeX; x++) {
          const idx = (y * sizeX + x) * 4;
          const [r, g, b] = grid[y][x];
          imageData.data[idx] = Math.floor(sharpen(r) * 255);
          imageData.data[idx + 1] = Math.floor(sharpen(g) * 255);
          imageData.data[idx + 2] = Math.floor(sharpen(b) * 255);
          imageData.data[idx + 3] = 255;
        }
      }
      ctx.putImageData(imageData, 0, 0);
      ctx.save();
      ctx.scale(scale, scale);
      ctx.drawImage(ctx.canvas, 0, 0);
      ctx.restore();
    };

    const canvas = canvasRef.current;
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");

    let lastTime = performance.now();
    const interval = 1000 / fps;

    const loop = (now) => {
      const delta = now - lastTime;
      if (delta >= interval) {
        lastTime = now - (delta % interval);
        updateGrid();
        drawGrid(ctx);
      }
      requestAnimationFrame(loop);
    };

    requestAnimationFrame(loop);
  }, [dimensions, scale, dt, fps]);

  return (
    <div className="letter-grid">
      <canvas ref={canvasRef} style={{ display: "block" }} />
    </div>);
};

export default Lenia;
